import React, { useEffect, useRef, useState, useCallback } from "react";
import Player from "@vimeo/player";

type Props = {
    url: string;
    params?: Record<string, string | number>;
    style?: React.CSSProperties;
    className?: string;
};

function parseVimeoUrl(url: string): { id: string; hash?: string } | null {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
    if (!m) return null;
    return { id: m[1], hash: m[2] };
}

/**
 * Vimeo player with guaranteed autoplay using background=1,
 * plus custom play/pause and volume controls via Vimeo Player SDK.
 */
const VimeoPlayer: React.FC<Props> = ({
    url,
    params = {},
    style,
    className,
}) => {
    const [mounted, setMounted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [showControls, setShowControls] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const playerRef = useRef<Player | null>(null);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted || !iframeRef.current) return;

        const player = new Player(iframeRef.current);
        playerRef.current = player;

        player.on("play", () => setIsPlaying(true));
        player.on("pause", () => setIsPlaying(false));

        return () => {
            if (playerRef.current) {
                playerRef.current.destroy().catch(() => {});
                playerRef.current = null;
            }
        };
    }, [mounted]);

    const togglePlay = useCallback(() => {
        if (!playerRef.current) return;
        if (isPlaying) {
            playerRef.current.pause();
        } else {
            playerRef.current.play();
        }
    }, [isPlaying]);

    const toggleMute = useCallback(() => {
        if (!playerRef.current) return;
        const next = !isMuted;
        playerRef.current.setMuted(next);
        setIsMuted(next);
    }, [isMuted]);

    const handleMouseEnter = useCallback(() => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        setShowControls(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hideTimerRef.current = setTimeout(() => setShowControls(false), 1500);
    }, []);

    if (!mounted) {
        return (
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    ...style,
                }}
                className={className}
            />
        );
    }

    const parsed = parseVimeoUrl(url);
    if (!parsed) return null;

    const embedParams = new URLSearchParams();
    embedParams.set("background", "1"); // guarantees autoplay
    embedParams.set("muted", "1");
    embedParams.set("loop", "1");
    if (parsed.hash) {
        embedParams.set("h", parsed.hash);
    }
    for (const [key, value] of Object.entries(params)) {
        if (key !== "unmute_button") {
            embedParams.set(key, String(value));
        }
    }

    const embedSrc = `https://player.vimeo.com/video/${
        parsed.id
    }?${embedParams.toString()}`;

    const btnStyle: React.CSSProperties = {
        background: "rgba(0, 0, 0, 0.5)",
        color: "#fff",
        border: "none",
        borderRadius: "50%",
        width: 36,
        height: 36,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        lineHeight: 1,
        padding: 0,
        transition: "opacity 0.3s",
    };

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                ...style,
            }}
            className={className}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <iframe
                ref={iframeRef}
                src={embedSrc}
                style={{
                    border: "none",
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                }}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
            />
            {/* Custom controls overlay */}
            <div
                style={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    display: "flex",
                    gap: 8,
                    opacity: showControls ? 1 : 0,
                    transition: "opacity 0.3s",
                    pointerEvents: showControls ? "auto" : "none",
                    zIndex: 10,
                }}
            >
                <button
                    onClick={togglePlay}
                    style={btnStyle}
                    aria-label={isPlaying ? "Pause" : "Play"}
                    title={isPlaying ? "Pause" : "Play"}
                >
                    {isPlaying ? (
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="currentColor"
                        >
                            <rect
                                x="2"
                                y="1"
                                width="3.5"
                                height="12"
                                rx="0.5"
                            />
                            <rect
                                x="8.5"
                                y="1"
                                width="3.5"
                                height="12"
                                rx="0.5"
                            />
                        </svg>
                    ) : (
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="currentColor"
                        >
                            <polygon points="2,1 13,7 2,13" />
                        </svg>
                    )}
                </button>
                <button
                    onClick={toggleMute}
                    style={btnStyle}
                    aria-label={isMuted ? "Unmute" : "Mute"}
                    title={isMuted ? "Unmute" : "Mute"}
                >
                    {isMuted ? (
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polygon
                                points="11,5 6,9 2,9 2,15 6,15 11,19"
                                fill="currentColor"
                            />
                            <line x1="23" y1="9" x2="17" y2="15" />
                            <line x1="17" y1="9" x2="23" y2="15" />
                        </svg>
                    ) : (
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polygon
                                points="11,5 6,9 2,9 2,15 6,15 11,19"
                                fill="currentColor"
                            />
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        </svg>
                    )}
                </button>
            </div>
        </div>
    );
};

export default VimeoPlayer;
